import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from '../../components/Header';
import EscenarioBar from '../../components/calculadora/EscenarioBar';
import PlanVariables from '../../components/calculadora/PlanVariables';
import PrestacionesTable from '../../components/calculadora/PrestacionesTable';
import ResultadoKPIs from '../../components/calculadora/ResultadoKPIs';
import BreakEvenChart from '../../components/calculadora/BreakEvenChart';
import ConsolidadoMix from '../../components/calculadora/ConsolidadoMix';
import ComparadorEscenarios from '../../components/calculadora/ComparadorEscenarios';
import NuevoPlanModal from '../../components/calculadora/NuevoPlanModal';
import DashboardErrorBoundary from '../../components/dashboard/DashboardErrorBoundary';
import calculadoraService from '../../services/calculadoraService';
import dashboardService from '../../services/dashboardService';
import {
  Escenario,
  EscenarioResumen,
  EscenarioData,
  PlanConfig,
  FilaPrestacion,
} from '../../types/Calculadora';
import { DashboardDataRaw } from '../../types/Dashboard';
import { PLANES } from '../../data/planes';
import {
  escenarioPorDefecto,
  reconciliarEscenario,
  filaEnBlanco,
  descuentoDePlan,
  planFicticio,
} from '../../data/calculadoraDefaults';
import {
  calcularConsolidado,
  prellenarConDatosReales,
  aplicarFilasATodos,
  breakEvenDeConsolidado,
  breakEvenDePlan,
  formatCurrency,
  formatNumber,
  DatosRealesResumen,
} from '../../components/calculadora/calculadoraHelpers';
import { Button, Card, Modal, cn, staggerParent, enterChild, springState } from '../../components/ui';
import { CloseIcon, PencilIcon, PlusIcon } from '../../components/icons';

const CONSOLIDADO = 'consolidado';
const COMPARAR = 'comparar';

/** Transición al cambiar de sub-pestaña. Suave: es alta frecuencia. */
const tabSwitch = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
  transition: { duration: 0.2, ease: 'easeOut' as const },
};

/** Resumen en castellano de lo que trajo el prellenado desde Salesforce. */
function mensajeDatosReales(r: DatosRealesResumen): string {
  const planes = `${r.planesActualizados} plan${r.planesActualizados === 1 ? '' : 'es'}`;
  const base = `Se cargaron ${formatNumber(r.suscriptoresTotales)} suscriptores en ${planes}.`;
  if (r.sinFechaNacimiento === 0) return base;

  const uno = r.sinFechaNacimiento === 1;
  return (
    `${base} ${formatNumber(r.sinFechaNacimiento)} activo${uno ? '' : 's'} del Exclusive 8080 ` +
    `${uno ? 'no tiene' : 'no tienen'} fecha de nacimiento, así que ${uno ? 'quedó' : 'quedaron'} ` +
    'fuera del mix etario.'
  );
}

const CalculadoraPage: React.FC = () => {
  const [escenarios, setEscenarios] = useState<EscenarioResumen[]>([]);
  const [escenarioActivo, setEscenarioActivo] = useState<Escenario | null>(null);
  const [data, setData] = useState<EscenarioData>(() => escenarioPorDefecto());
  /** JSON del último estado guardado (o del punto de partida), para detectar cambios. */
  const [baseline, setBaseline] = useState<string>('');
  const [tab, setTab] = useState<string>(CONSOLIDADO);

  const [dashboardData, setDashboardData] = useState<DashboardDataRaw | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [cargandoDatosReales, setCargandoDatosReales] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [nuevoPlanAbierto, setNuevoPlanAbierto] = useState(false);
  /** planId del plan ficticio que se está por borrar, con confirmación. */
  const [planABorrar, setPlanABorrar] = useState<string | null>(null);
  /** planId del plan ficticio que se está renombrando, y el nombre tipeado. */
  const [planARenombrar, setPlanARenombrar] = useState<string | null>(null);
  const [nombreEditado, setNombreEditado] = useState('');

  const dirty = baseline !== '' && JSON.stringify(data) !== baseline;

  const consolidado = useMemo(() => calcularConsolidado(data), [data]);

  const planActivo = data.planes.find((p) => p.planId === tab) ?? null;
  const resultadoActivo = consolidado.porPlan.find((p) => p.planId === tab) ?? null;

  // ── Carga inicial ────────────────────────────────────────────────────────

  const traerDashboard = useCallback(async (): Promise<DashboardDataRaw | null> => {
    const res = await dashboardService.getDashboardData();
    if (!res.success) return null;
    const payload: DashboardDataRaw = {
      fetchedAt: res.fetchedAt,
      stale: res.stale,
      premium: res.premium,
      freeActivos: res.freeActivos,
      precios: res.precios,
      manualRevenueHistory: res.manualRevenueHistory,
    };
    setDashboardData(payload);
    return payload;
  }, []);

  useEffect(() => {
    let cancelado = false;

    (async () => {
      setCargando(true);
      setError(null);
      try {
        const [lista, dash] = await Promise.all([
          calculadoraService.listarEscenarios(),
          traerDashboard().catch(() => null),
        ]);
        if (cancelado) return;

        setEscenarios(lista);

        if (lista.length > 0) {
          // El listado viene ordenado por última modificación.
          const completo = await calculadoraService.obtenerEscenario(lista[0].id);
          if (cancelado) return;
          if (completo?.data) {
            const reconciliado = reconciliarEscenario(completo.data);
            setEscenarioActivo(completo);
            setData(reconciliado);
            setBaseline(JSON.stringify(reconciliado));
            return;
          }
        }

        // Sin escenarios guardados: arrancamos de la semilla y, si se pudo
        // hablar con Salesforce, ya con la población real cargada.
        let inicial = escenarioPorDefecto();
        if (dash) {
          inicial = prellenarConDatosReales(
            inicial,
            dash.premium,
            dash.freeActivos,
            dash.precios,
          ).data;
        }
        setData(inicial);
        setBaseline(JSON.stringify(inicial));
      } catch (err) {
        if (!cancelado) {
          setError(err instanceof Error ? err.message : 'Error al cargar la calculadora');
        }
      } finally {
        if (!cancelado) setCargando(false);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [traerDashboard]);

  // ── Edición ──────────────────────────────────────────────────────────────

  const actualizarPlan = useCallback((planId: string, patch: Partial<PlanConfig>) => {
    setData((prev) => ({
      ...prev,
      planes: prev.planes.map((p) => (p.planId === planId ? { ...p, ...patch } : p)),
    }));
  }, []);

  const actualizarFila = useCallback(
    (planId: string, index: number, patch: Partial<FilaPrestacion>) => {
      setData((prev) => ({
        ...prev,
        planes: prev.planes.map((p) =>
          p.planId === planId
            ? { ...p, filas: p.filas.map((f, i) => (i === index ? { ...f, ...patch } : f)) }
            : p,
        ),
      }));
    },
    [],
  );

  const agregarFila = useCallback((planId: string) => {
    setData((prev) => ({
      ...prev,
      planes: prev.planes.map((p) =>
        p.planId === planId ? { ...p, filas: [...p.filas, filaEnBlanco('Práctica nueva')] } : p,
      ),
    }));
  }, []);

  const quitarFila = useCallback((planId: string, index: number) => {
    setData((prev) => ({
      ...prev,
      planes: prev.planes.map((p) =>
        p.planId === planId ? { ...p, filas: p.filas.filter((_, i) => i !== index) } : p,
      ),
    }));
  }, []);

  const crearPlanFicticio = useCallback(
    (nombre: string, basePlanId: string | null) => {
      const base = basePlanId ? data.planes.find((p) => p.planId === basePlanId) : undefined;
      const nuevo = planFicticio(nombre, base);
      setData((prev) => ({ ...prev, planes: [...prev.planes, nuevo] }));
      setNuevoPlanAbierto(false);
      setTab(nuevo.planId);
      setAviso(
        base
          ? `Plan ficticio "${nombre}" creado a partir de ${base.nombre}. Cargale los suscriptores y la cuota.`
          : `Plan ficticio "${nombre}" creado en blanco. Cargale la cuota, la cobertura y los suscriptores.`,
      );
    },
    [data.planes],
  );

  const abrirRenombrar = useCallback((planId: string, nombre: string) => {
    setNombreEditado(nombre);
    setPlanARenombrar(planId);
  }, []);

  const confirmarRenombrar = useCallback(
    (planId: string, nombre: string) => {
      actualizarPlan(planId, { nombre });
      setPlanARenombrar(null);
    },
    [actualizarPlan],
  );

  const borrarPlanFicticio = useCallback((planId: string, nombre: string) => {
    setData((prev) => ({ ...prev, planes: prev.planes.filter((p) => p.planId !== planId) }));
    setPlanABorrar(null);
    setTab((actual) => (actual === planId ? CONSOLIDADO : actual));
    setAviso(`Plan ficticio "${nombre}" borrado del escenario.`);
  }, []);

  const copiarPreciosATodos = useCallback((planId: string) => {
    setData((prev) =>
      aplicarFilasATodos(prev, planId, (destinoId, fila) => {
        const plan = PLANES.find((p) => p.id === destinoId);
        // Un plan ficticio no está en el catálogo, así que no hay un % oficial
        // que respetar: hereda el de la fila origen.
        return plan ? descuentoDePlan(plan, fila.categoria) : fila.descuentoPct;
      }),
    );
    setAviso('Precios y cantidades copiados al resto de los planes. Cada plan mantuvo su % de descuento.');
  }, []);

  const restaurarDistribucionReal = useCallback(
    (planId: string) => {
      if (!dashboardData) return;
      setData((prev) => {
        const soloEstePlan: EscenarioData = {
          ...prev,
          planes: prev.planes.filter((p) => p.planId === planId),
        };
        const { data: actualizado } = prellenarConDatosReales(
          soloEstePlan,
          dashboardData.premium,
          dashboardData.freeActivos,
          dashboardData.precios,
        );
        const nuevo = actualizado.planes[0];
        if (!nuevo) return prev;
        return {
          ...prev,
          planes: prev.planes.map((p) =>
            p.planId === planId ? { ...p, distribucionEdad: nuevo.distribucionEdad } : p,
          ),
        };
      });
    },
    [dashboardData],
  );

  const handleTraerDatosReales = useCallback(async () => {
    setCargandoDatosReales(true);
    setError(null);
    try {
      const dash = dashboardData ?? (await traerDashboard());
      if (!dash) {
        setError('No se pudieron traer los datos de Salesforce.');
        return;
      }
      // Se calcula sobre el estado actual en vez de dentro de un updater:
      // el updater corre en el próximo render y el resumen llegaría vacío.
      const { data: actualizado, resumen } = prellenarConDatosReales(
        data,
        dash.premium,
        dash.freeActivos,
        dash.precios,
      );
      setData(actualizado);
      setAviso(mensajeDatosReales(resumen));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al traer los datos de Salesforce');
    } finally {
      setCargandoDatosReales(false);
    }
  }, [data, dashboardData, traerDashboard]);

  // ── Escenarios ───────────────────────────────────────────────────────────

  const refrescarLista = useCallback(async () => {
    try {
      setEscenarios(await calculadoraService.listarEscenarios());
    } catch {
      /* el listado no es crítico: si falla se reintenta en la próxima acción */
    }
  }, []);

  const seleccionarEscenario = useCallback(async (id: number) => {
    setError(null);
    try {
      const completo = await calculadoraService.obtenerEscenario(id);
      if (!completo?.data) {
        setError('El escenario no se pudo leer. Puede estar corrupto.');
        return;
      }
      const reconciliado = reconciliarEscenario(completo.data);
      setEscenarioActivo(completo);
      setData(reconciliado);
      setBaseline(JSON.stringify(reconciliado));
      setAviso(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al abrir el escenario');
    }
  }, []);

  const nuevoEscenario = useCallback(() => {
    const inicial = escenarioPorDefecto();
    setEscenarioActivo(null);
    setData(inicial);
    setBaseline(JSON.stringify(inicial));
    setAviso(null);
  }, []);

  const guardar = useCallback(async () => {
    if (!escenarioActivo) return;
    setGuardando(true);
    setError(null);
    const res = await calculadoraService.actualizarEscenario(escenarioActivo.id, { data });
    setGuardando(false);

    if (!res.success || !res.scenario) {
      setError(res.error ?? 'No se pudo guardar el escenario');
      return;
    }
    setEscenarioActivo(res.scenario);
    setBaseline(JSON.stringify(data));
    setAviso('Escenario guardado.');
    refrescarLista();
  }, [escenarioActivo, data, refrescarLista]);

  const guardarComo = useCallback(
    async (nombre: string, descripcion: string) => {
      setGuardando(true);
      setError(null);
      const res = await calculadoraService.crearEscenario({
        name: nombre,
        description: descripcion || undefined,
        data,
      });
      setGuardando(false);

      if (!res.success || !res.scenario) {
        setError(res.error ?? 'No se pudo guardar el escenario');
        return;
      }
      setEscenarioActivo(res.scenario);
      setBaseline(JSON.stringify(data));
      setAviso(`Escenario "${nombre}" guardado.`);
      refrescarLista();
    },
    [data, refrescarLista],
  );

  const borrar = useCallback(async () => {
    if (!escenarioActivo) return;
    setError(null);
    const res = await calculadoraService.borrarEscenario(escenarioActivo.id);
    if (!res.success) {
      setError(res.error ?? 'No se pudo borrar el escenario');
      return;
    }
    setAviso(`Escenario "${escenarioActivo.name}" borrado.`);
    nuevoEscenario();
    refrescarLista();
  }, [escenarioActivo, nuevoEscenario, refrescarLista]);

  // ── Render ───────────────────────────────────────────────────────────────

  // El "+" se intercala entre los planes y Comparar, así que la lista se corta
  // ahí y Comparar se renderiza aparte.
  const tabsDePlanes = [
    { id: CONSOLIDADO, label: 'Consolidado' },
    ...data.planes.map((p) => ({ id: p.planId, label: p.nombre, ficticio: !!p.ficticio })),
  ];

  /** Renombrar y borrar viven en la pestaña, que es donde se busca un plan. */
  const accionesDeTab = 'rounded-md p-1 text-slate-400 transition-colors duration-150 ' +
    'hover:bg-slate-100 hover:text-gappex-secondary outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-gappex-primary/40';

  const renderTab = (t: { id: string; label: string; ficticio?: boolean }) => {
    const active = tab === t.id;
    const conAcciones = Boolean(t.ficticio) && active;
    return (
      <div key={t.id} className="relative flex items-center">
        <button
          role="tab"
          aria-selected={active}
          onClick={() => setTab(t.id)}
          className={cn(
            'py-2.5 text-sm font-medium whitespace-nowrap transition-colors duration-150',
            conAcciones ? 'ps-4 pe-2' : 'px-4',
            active ? 'text-gappex-primary' : 'text-slate-500 hover:text-gappex-secondary',
          )}
        >
          {t.label}
        </button>

        {conAcciones && (
          <span className="flex items-center gap-0.5 pe-2">
            <button
              type="button"
              onClick={() => abrirRenombrar(t.id, t.label)}
              aria-label={`Renombrar el plan ${t.label}`}
              title="Renombrar este plan"
              className={accionesDeTab}
            >
              <PencilIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
            <button
              type="button"
              onClick={() => setPlanABorrar(t.id)}
              aria-label={`Borrar el plan ${t.label}`}
              title="Borrar este plan"
              className={cn(accionesDeTab, 'hover:text-red-600')}
            >
              <CloseIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </span>
        )}

        {active && (
          <motion.span
            layoutId="calc-tab-underline"
            transition={springState}
            className="absolute left-2 right-2 bottom-0 h-0.5 rounded-full bg-gappex-primary"
          />
        )}
      </div>
    );
  };

  const planDeBaseSugerido = data.planes.some((p) => p.planId === tab) ? tab : null;
  const planEnBorrado = data.planes.find((p) => p.planId === planABorrar) ?? null;
  const planEnRenombrado = data.planes.find((p) => p.planId === planARenombrar) ?? null;
  const nombreLimpio = nombreEditado.trim();
  const nombreRepetido = data.planes.some(
    (p) => p.planId !== planARenombrar && p.nombre.toLowerCase() === nombreLimpio.toLowerCase(),
  );

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8">
        <motion.div
          variants={staggerParent}
          initial="hidden"
          animate="visible"
          className="space-y-6"
        >
          <motion.div variants={enterChild}>
            <h1 className="text-2xl font-poppins font-semibold text-gappex-secondary">
              Calculadora de rentabilidad
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Cuánto deja cada plan según lo que cuesta cubrirlo. Los números son de margen
              prestacional: no incluyen comisiones de cobro, morosidad ni estructura.
            </p>
          </motion.div>

          {cargando ? (
            <div className="space-y-6">
              <Card className="p-4 animate-pulse">
                <div className="h-9 bg-slate-100 rounded-lg w-full" />
              </Card>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {[0, 1, 2, 3].map((i) => (
                  <Card key={i} className="p-5 animate-pulse">
                    <div className="h-2.5 bg-slate-100 rounded w-2/3 mb-3.5" />
                    <div className="h-7 bg-slate-100 rounded w-1/2" />
                  </Card>
                ))}
              </div>
              <Card className="p-6 animate-pulse">
                <div className="h-64 bg-slate-100 rounded-xl" />
              </Card>
            </div>
          ) : (
            <DashboardErrorBoundary>
              <motion.div variants={enterChild}>
                <EscenarioBar
                  escenarios={escenarios}
                  escenarioActivo={escenarioActivo}
                  dirty={dirty}
                  guardando={guardando}
                  cargandoDatosReales={cargandoDatosReales}
                  onSeleccionar={seleccionarEscenario}
                  onNuevo={nuevoEscenario}
                  onGuardar={guardar}
                  onGuardarComo={guardarComo}
                  onBorrar={borrar}
                  onTraerDatosReales={handleTraerDatosReales}
                />
              </motion.div>

              <AnimatePresence>
                {error && (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="rounded-xl bg-red-50 shadow-elevate p-4 border-l-4 border-red-500"
                  >
                    <p className="text-sm text-red-700">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence>
                {aviso && (
                  <motion.div
                    key="aviso"
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    className="rounded-xl bg-sky-50 shadow-elevate p-4 border-l-4 border-sky-500 flex items-start justify-between gap-3"
                  >
                    <p className="text-sm text-sky-800">{aviso}</p>
                    <button
                      onClick={() => setAviso(null)}
                      aria-label="Cerrar aviso"
                      className="text-sky-400 hover:text-sky-600 shrink-0 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Sub-pestañas: consolidado + una por plan, con indicador deslizante.
                  El borde va en el contenedor y el scroll horizontal en el hijo, para
                  que overflow-x no dispare una barra vertical fantasma. */}
              <motion.div variants={enterChild} className="border-b border-slate-200">
                <div className="overflow-x-auto">
                  <nav className="flex items-center gap-1 min-w-max" role="tablist">
                    {tabsDePlanes.map(renderTab)}

                    <button
                      type="button"
                      onClick={() => setNuevoPlanAbierto(true)}
                      aria-label="Agregar un plan ficticio"
                      title="Agregar un plan ficticio"
                      className="ms-1 mb-1.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg
                        text-slate-400 transition-colors duration-150 hover:bg-slate-100 hover:text-gappex-primary
                        outline-none focus-visible:ring-2 focus-visible:ring-gappex-primary/40"
                    >
                      <PlusIcon className="h-4 w-4" strokeWidth={2} />
                    </button>

                    <span className="mx-2 mb-1.5 h-5 w-px shrink-0 bg-slate-200" aria-hidden="true" />

                    {renderTab({ id: COMPARAR, label: 'Comparar' })}
                  </nav>
                </div>
              </motion.div>

              <motion.div variants={enterChild}>
                <AnimatePresence mode="wait" initial={false}>
                  <motion.div
                    key={tab}
                    initial={tabSwitch.initial}
                    animate={tabSwitch.animate}
                    exit={tabSwitch.exit}
                    transition={tabSwitch.transition}
                    className="space-y-6"
                  >
                    {tab === CONSOLIDADO ? (
                      <>
                        <ResultadoKPIs
                          resumen={consolidado}
                          detalleIngreso={`${formatNumber(consolidado.suscriptores)} suscriptores · ${formatCurrency(
                            consolidado.ingresoMensual,
                          )}/mes`}
                        />
                        <ConsolidadoMix consolidado={consolidado} onVerPlan={setTab} />
                        <BreakEvenChart
                          be={breakEvenDeConsolidado(consolidado.ingresoAnual, consolidado.gastoAnualMin, consolidado.gastoAnualProm, consolidado.gastoAnualMax)}
                          titulo="Punto de equilibrio del mix"
                        />
                      </>
                    ) : tab === COMPARAR ? (
                      <ComparadorEscenarios
                        escenarios={escenarios}
                        currentData={data}
                        currentName={escenarioActivo ? escenarioActivo.name : 'Actual (sin guardar)'}
                      />
                    ) : planActivo && resultadoActivo ? (
                      <>
                        <ResultadoKPIs resumen={resultadoActivo} />
                        <div className="grid grid-cols-1 lg:grid-cols-[360px_1fr] gap-6 items-start">
                          <PlanVariables
                            config={planActivo}
                            resultado={resultadoActivo}
                            onChange={(patch) => actualizarPlan(planActivo.planId, patch)}
                            onRestaurarDistribucion={() => restaurarDistribucionReal(planActivo.planId)}
                            distribucionRealDisponible={dashboardData !== null}
                          />
                          <BreakEvenChart
                            be={breakEvenDePlan(
                              resultadoActivo.ingresoAnual,
                              resultadoActivo.suscriptores,
                              resultadoActivo.costoPerCapitaMin,
                              resultadoActivo.costoPerCapitaProm,
                              resultadoActivo.costoPerCapitaMax,
                              planActivo.tasaUso,
                            )}
                            titulo={`Punto de equilibrio de ${planActivo.nombre}`}
                          />
                        </div>
                        <PrestacionesTable
                          filas={planActivo.filas}
                          calculadas={resultadoActivo.filas}
                          planNombre={planActivo.nombre}
                          onChangeFila={(index, patch) => actualizarFila(planActivo.planId, index, patch)}
                          onRemoveFila={(index) => quitarFila(planActivo.planId, index)}
                          onAddFila={() => agregarFila(planActivo.planId)}
                          onAplicarATodos={() => copiarPreciosATodos(planActivo.planId)}
                        />
                      </>
                    ) : null}
                  </motion.div>
                </AnimatePresence>
              </motion.div>
            </DashboardErrorBoundary>
          )}
        </motion.div>

        <AnimatePresence>
          {nuevoPlanAbierto && (
            <NuevoPlanModal
              key="nuevo-plan"
              planes={data.planes}
              baseSugerida={planDeBaseSugerido}
              onCrear={crearPlanFicticio}
              onClose={() => setNuevoPlanAbierto(false)}
            />
          )}

          {planEnRenombrado && (
            <Modal
              key="renombrar-plan"
              titulo="Renombrar plan"
              onClose={() => setPlanARenombrar(null)}
            >
              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="renombrar-plan-input"
                    className="block text-xs font-semibold text-gray-600 mb-1.5"
                  >
                    Nombre del plan
                  </label>
                  <input
                    id="renombrar-plan-input"
                    type="text"
                    value={nombreEditado}
                    autoFocus
                    maxLength={60}
                    onChange={(e) => setNombreEditado(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && nombreLimpio !== '' && !nombreRepetido) {
                        confirmarRenombrar(planEnRenombrado.planId, nombreLimpio);
                      }
                    }}
                    className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2
                      focus:outline-none focus:ring-2 focus:ring-gappex-primary/30 focus:border-gappex-primary"
                  />
                  {nombreRepetido && (
                    <p className="mt-1.5 text-[11px] text-amber-600 leading-snug">
                      Ya hay un plan con ese nombre. Poné otro para no mezclarlo al traer los datos
                      reales.
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={() => setPlanARenombrar(null)}>
                    Cancelar
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    disabled={nombreLimpio === '' || nombreRepetido}
                    onClick={() => confirmarRenombrar(planEnRenombrado.planId, nombreLimpio)}
                  >
                    Guardar
                  </Button>
                </div>
              </div>
            </Modal>
          )}

          {planEnBorrado && (
            <Modal key="borrar-plan" titulo="Borrar plan ficticio" onClose={() => setPlanABorrar(null)}>
              <p className="text-sm text-gray-600">
                Se va a sacar{' '}
                <strong className="text-gappex-secondary">{planEnBorrado.nombre}</strong> de este
                escenario, con sus precios y su población. Se borra del todo cuando guardes.
              </p>
              <div className="flex justify-end gap-2 mt-5">
                <Button type="button" variant="secondary" onClick={() => setPlanABorrar(null)}>
                  Cancelar
                </Button>
                <Button
                  type="button"
                  variant="danger"
                  onClick={() => borrarPlanFicticio(planEnBorrado.planId, planEnBorrado.nombre)}
                >
                  Borrar
                </Button>
              </div>
            </Modal>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default CalculadoraPage;
